/**
 * DeleteConfirmDialog.js
 *
 * A reusable dialog component for confirming deletion operations.
 * Provides a consistent interface for confirming destructive actions across the application.
 *
 * Key Features:
 * - Customizable dialog text and buttons
 * - Handles deletion operations with error handling
 * - Updates UI state after successful deletion
 * - Provides feedback through messages
 *
 * Props:
 * @param {boolean} open - Controls dialog visibility
 * @param {Function} setOpen - Function to control dialog visibility
 * @param {Array} selectedRows - Array of selected row IDs to delete
 * @param {Function} setselectedRows - Function to update selected rows
 * @param {Function} setDeleteMode - Function to control delete mode
 * @param {Function} refetchRows - Function to refresh data after deletion
 * @param {Function} setMessage - Function to display status messages
 * @param {Function} deleteFunction - API function to perform the deletion
 * @param {string} dialogTitle - Title of the confirmation dialog
 * @param {string} dialogContent - Content text of the confirmation dialog
 * @param {string} cancelButtonText - Text for the cancel button
 * @param {string} confirmButtonText - Text for the confirm button
 */

// DeleteConfirmDialog.js
import React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";

const DeleteConfirmDialog = ({
  open,
  setOpen,
  selectedRows,
  setselectedRows,
  setDeleteMode,
  refetchRows,
  setMessage,
  deleteFunction, // Added custom text props with defaults
  dialogTitle = "Confirm Deletion",
  dialogContent = "Are you sure you want to delete the selected rows?",
  cancelButtonText = "Cancel",
  confirmButtonText = "Delete",
}) => {
  /**
   * Handles the confirmation of deletion
   * Calls the delete function, updates UI state, and shows appropriate messages
   */
  const handleDeleteConfirm = async () => {
    try {
      await deleteFunction(selectedRows);
      refetchRows();
      setMessage({
        text: "Deleted successfully",
        severity: "success",
        open: true,
      });
    } catch (error) {
      setMessage({
        text: error.message || "Error deleting",
        severity: "error",
        open: true,
      });
    } finally {
      setOpen(false);
      setDeleteMode(false);
      setselectedRows([]);
    }
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <DialogTitle> {dialogTitle}</DialogTitle>
      <DialogContent>
        <DialogContentText>{dialogContent}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>{cancelButtonText}</Button>
        <Button onClick={handleDeleteConfirm} color="error" autoFocus>
          {confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmDialog;
